import { saveScrollY, getScrollY } from '../services/helperService';

const TABLE_PAGE_KEY = 'table_page_number';

/**
 * Resets scroll position to top of the main container
 * Call this in useEffect when a page loads and you want to start at the top
 */
export const resetScrollToTop = () => {
  const scroller = document.getElementById('mainContainer') as HTMLDivElement | null;
  if (!scroller) return;
  scroller.scrollTop = 0;
};

/**
 * Saves the current scroll position before navigating away
 * Call this before navigating to a detail page when you want to restore position on return
 */
export const saveCurrentScrollPosition = () => {
  const scroller = document.getElementById('mainContainer') as HTMLDivElement | null;
  const y = scroller?.scrollTop ?? 0;
  saveScrollY(y);
};

/**
 * Restores the previously saved scroll position
 * Call this in useEffect when returning from a detail page
 * Uses requestAnimationFrame to wait for content to load before scrolling
 */
export const restoreScrollPosition = () => {
  const y = getScrollY();
  const scroller = document.getElementById('mainContainer') as HTMLDivElement | null;
  if (!scroller) return;

  const maxNeeded = y + scroller.clientHeight;

  const tryRestore = () => {
    if (scroller.scrollHeight >= maxNeeded) {
      scroller.scrollTop = y;
    } else {
      requestAnimationFrame(tryRestore);
    }
  };
  requestAnimationFrame(tryRestore);
};

/**
 * Saves the current table page number before navigating away
 * Call this before navigating to a detail page when you want to restore page on return
 */
export const saveTablePage = (page: number) => {
  sessionStorage.setItem(TABLE_PAGE_KEY, String(page));
};

/**
 * Gets the previously saved table page number
 * Returns null if no page was saved
 */
export const getTablePage = (): number | null => {
  const saved = sessionStorage.getItem(TABLE_PAGE_KEY);
  return saved ? parseInt(saved, 10) : null;
};

/**
 * Clears the saved table page number
 * Call this when navigating away from the page (not to detail)
 */
export const clearTablePage = () => {
  sessionStorage.removeItem(TABLE_PAGE_KEY);
};

/**
 * Saves the current window scroll position
 * Useful for preserving scroll position during page updates/reloads
 */
export const saveWindowScrollPosition = () => {
  const y = window.scrollY || document.documentElement.scrollTop;
  saveScrollY(y);
};

/**
 * Restores the previously saved window scroll position
 * Uses requestAnimationFrame to wait for content to load before scrolling
 */
export const restoreWindowScrollPosition = () => {
  const y = getScrollY();
  // getScrollY returns a number (defaults to 0), so we check if it's a valid number
  if (typeof y !== 'number' || isNaN(y)) return;

  const tryRestore = () => {
    const maxNeeded = y + window.innerHeight;
    if (document.documentElement.scrollHeight >= maxNeeded) {
      window.scrollTo(0, y);
    } else {
      requestAnimationFrame(tryRestore);
    }
  };
  requestAnimationFrame(tryRestore);
};
